import { useState } from 'react';
import { createBillingAccountCoco, fetchCustomer } from '../../common/fetch.js';
import { t } from '../../common/i18n/index.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { toBillingAccountCreateRequestCoco, toContactOptions } from './billingAccountCocoFormModel.js';
import type { BillingAccountCocoFormValues } from './billingAccountCocoFormModel.js';
import type { BillingAccountType } from '../../common/utils/billingAccountUtilsCoCo.js';
import type { BillingAccountCoco } from '../../generated/api/billingAccountCoco.js';
import type { RelatedPartyRef } from '../../generated/api/relatedPartyRef.js';

interface UseCreateBillingAccountCocoArgs {
  billingAccountType: BillingAccountType;
  relatedParties: RelatedPartyRef[];
  mdmId?: string;
  onError?: (message: string) => void;
}

export const useCreateBillingAccountCoco = ({
  billingAccountType,
  relatedParties,
  mdmId,
  onError,
}: UseCreateBillingAccountCocoArgs) => {
  const [isSaving, setIsSaving] = useState(false);

  const createBillingAccount = async (
    values: BillingAccountCocoFormValues
  ): Promise<BillingAccountCoco | undefined> => {
    setIsSaving(true);

    try {
      // Re-fetch so a contact removed elsewhere since the form was opened isn't missed.
      const latestCustomer = await fetchCustomer(mdmId);
      const contacts = toContactOptions(latestCustomer.relatedParty);

      if (!contacts.some(contact => contact.contactId === values.billingAccount.billingContactId)) {
        onError?.(t.WIMV('Selected contact is not valid, please check the selection.'));
        return undefined;
      }

      const response = await createBillingAccountCoco(
        toBillingAccountCreateRequestCoco(values, {
          billingAccountType,
          relatedParties,
          customer: latestCustomer,
          contacts,
        }),
        mdmId
      );

      if (!response.id) {
        throw new Error('The billing account was created without an id.');
      }

      addNotification(t.OS99('Billing account was successfully created.'));
      return response;
    } catch {
      onError?.(t.EBJW('Adding a new billing account failed. Please try again later.'));
      return undefined;
    } finally {
      setIsSaving(false);
    }
  };

  return { createBillingAccount, isSaving };
};
